import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import RobotoRegular from '../fonts/Roboto-Regular.ttf';
import RobotoBold from '../fonts/Roboto-Bold.ttf';
import RobotoItalic from '../fonts/Roboto-Italic.ttf';
import encabezadoA4 from './informes/encabezado_A4.png';
import pieHojaA4 from './informes/pie_hoja_A4.png';

// Fuente usada por el informe. Se usan TTF ESTÁTICOS: la versión variable
// (Roboto-VariableFont_wdth,wght.ttf) no la soporta fontkit/textkit de
// react-pdf y rompe el renderizado con "unitsPerEm undefined".
Font.register({
    family: 'Roboto',
    fonts: [
        { src: RobotoRegular, fontWeight: 400 },
        { src: RobotoBold, fontWeight: 700 },
        { src: RobotoItalic, fontWeight: 400, fontStyle: 'italic' },
    ],
});

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#ffffff',
        fontFamily: 'Roboto',
        fontSize: 10,
        color: '#333333',
        paddingTop: 84,      // deja lugar para el encabezado (imagen fija) + margen
        paddingBottom: 122,  // deja lugar para el pie de página (imagen fija) + margen
        paddingHorizontal: 57,  // margen izquierdo/derecho de 2 cm
    },
    encabezado: {
        position: 'absolute',
        top: 24,
        left: 57,
        right: 57,
    },
    encabezadoImg: {
        width: '100%',
    },
    pie: {
        position: 'absolute',
        bottom: 24,
        left: 57,
        right: 57,
    },
    pieImg: {
        width: '100%',
    },
    tituloPage: {
        fontSize: 16,
        fontWeight: 700,
        textAlign: 'center',
        lineHeight: 1.5,
        marginBottom: 2,
    },
    subtitulo: {
        fontSize: 12,
        fontWeight: 700,
        textAlign: 'center',
        color: '#555555',
        marginBottom: 14,
    },
    fila: {
        marginBottom: 4,
    },
    filaLabel: {
        fontWeight: 700,
    },
    separador: {
        borderBottomWidth: 1,
        borderBottomColor: '#d1d5db',
        marginTop: 10,
        marginBottom: 8,
    },
    seccionTitulo: {
        fontSize: 13,
        fontWeight: 700,
        marginBottom: 12,
    },
    listaItem: {
        marginBottom: 2,
    },
    listaItemBold: {
        fontWeight: 700,
    },
    textoItalico: {
        fontStyle: 'italic',
        color: '#888888',
    },
    textoPre: {
        marginBottom: 10,
        textAlign: 'justify',
    },
    obsTitulo: {
        fontSize: 16,
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: 12,
        marginTop: 10,
    },
    obsIntro: {
        marginBottom: 6,
        textAlign: 'justify',
    },
    fecha: {
        textAlign: 'right',
        marginTop: 16,
        fontSize: 9.5,
        color: '#666666',
    },
    numeroPagina: {
        textAlign: 'center',
        fontSize: 9,
        color: '#666666',
        marginTop: 2,
    },
});

const InformeTecnicoPDF = ({ datos }) => {
    const {
        nombre_obra,
        expediente_proyecto,
        partido_nombre,
        lc_21_localidad,
        barrios_rppvap_multiples_str,
        barrios_otro_str,
        barrios_renabap_multiples_str,
        organismo_solicitante,
        modalidad,
        monto_obra,
        tipo_contratacion,
        plazo_obra,
        beneficiarios,
        datos_catastrales,
        cuadro_calles,
        array_tipo_subtipo_proyecto,
        descripcion_proyecto,
        observacionesReporte,
        fecha_salida,
    } = datos || {};

    const hayBarrios = barrios_rppvap_multiples_str || barrios_otro_str || barrios_renabap_multiples_str;

    // NOTA: react-pdf 4.x tiene un bug con caracteres '\n' dentro de un <Text>
    // ("Cannot read properties of undefined (reading 'unitsPerEm')"), por eso el
    // texto pre-wrap se renderiza como UNA línea por <Text> (con '\u00A0' para
    // conservar las líneas vacías) y nunca se inyecta un salto de línea literal.
    const Lineas = ({ texto, style }) => (texto || '')
        .replace(/\n{2,}/g, '\n')
        .replace(/^\n+|\n+$/g, '')
        .split('\n')
        .map((linea, i) => (
            <Text key={i} style={[style, { marginBottom: 2 }]}>
                {linea || '\u00A0'}
            </Text>
        ));

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View fixed style={styles.encabezado}>
                    <Image style={styles.encabezadoImg} src={encabezadoA4} />
                </View>

                <Text style={styles.tituloPage}>Informe técnico con observaciones</Text>
                <Text style={styles.subtitulo}>Dirección Provincial de Intervenciones Complementarias</Text>

                <View>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Nombre del Proyecto/alias: </Text>
                        {nombre_obra}
                    </Text>
                    <Text style={[styles.fila, { textAlign: 'right' }]}>
                        <Text style={styles.filaLabel}>Referente al Expediente: </Text>
                        {expediente_proyecto ? expediente_proyecto : 'No especificado'}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Partido: </Text>
                        {partido_nombre || 'No especificado'}
                        <Text style={styles.filaLabel}> - Localidad: </Text>
                        {lc_21_localidad || 'No especificado'}
                    </Text>
                    {hayBarrios ? (
                        <>
                            {barrios_rppvap_multiples_str && (
                                <Text style={styles.fila}>
                                    <Text style={styles.filaLabel}>Barrio RPPVAP: </Text>
                                    {barrios_rppvap_multiples_str}
                                </Text>
                            )}
                            {barrios_otro_str && (
                                <Text style={styles.fila}>
                                    <Text style={styles.filaLabel}>Barrio (Otros): </Text>
                                    {barrios_otro_str}
                                </Text>
                            )}
                            {barrios_renabap_multiples_str && (
                                <Text style={styles.fila}>
                                    <Text style={styles.filaLabel}>Barrio RENABAP: </Text>
                                    {barrios_renabap_multiples_str}
                                </Text>
                            )}
                        </>
                    ) : (
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Barrio: </Text>
                            No especificado
                        </Text>
                    )}
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Organismo Solicitante: </Text>
                        {organismo_solicitante}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Modalidad: </Text>
                        {modalidad}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Monto de obra en $: </Text>
                        {monto_obra}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Tipo de contratación: </Text>
                        {tipo_contratacion}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Plazo de obra: </Text>
                        {plazo_obra}
                        {plazo_obra ? ' días' : ''}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Beneficiarios: </Text>
                        {beneficiarios}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Ubicación del Proyecto</Text>
                    </Text>
                    {datos_catastrales && (
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>- Datos catastrales: </Text>
                            {datos_catastrales}
                        </Text>
                    )}
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>- Cuadro de calles: </Text>
                        {cuadro_calles}
                    </Text>
                </View>

                <View style={styles.separador} />

                <Text style={styles.seccionTitulo}>Descripción del proyecto</Text>
                {array_tipo_subtipo_proyecto && array_tipo_subtipo_proyecto.length > 0 ? (
                    <View>
                        {array_tipo_subtipo_proyecto.map((item, index) => (
                            <Text key={index} style={styles.listaItem}>
                                <Text style={styles.listaItemBold}>{item.tipo}: </Text>
                                {item.subtipo} - {item.cantidad} - {item.unidad}
                            </Text>
                        ))}
                    </View>
                ) : (
                    <Text style={[styles.listaItem, styles.textoItalico]}>No se especificaron ítems de obra.</Text>
                )}

                {descripcion_proyecto && descripcion_proyecto.trim().length > 0 && (
                    <View style={{ marginTop: 8 }}>
                        <Lineas texto={descripcion_proyecto} style={styles.textoPre} />
                    </View>
                )}

                <Text style={styles.obsTitulo} minPresenceAhead={70}>
                    Observaciones
                </Text>
                <Text style={styles.obsIntro}>
                    A partir del análisis de la propuesta y la documentación técnica
                    presentada, la Dirección Provincial de Intervenciones Complementarias
                    realiza las siguientes observaciones:
                </Text>
                <Lineas texto={observacionesReporte} style={styles.textoPre} />

                <Text style={styles.fecha}>Fecha de salida: {fecha_salida}</Text>

                <View fixed style={styles.pie}>
                    <Image style={styles.pieImg} src={pieHojaA4} />
                    <Text render={({ pageNumber }) => `-${pageNumber}-`} style={styles.numeroPagina} />
                </View>
            </Page>
        </Document>
    );
};

export default InformeTecnicoPDF;